import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import {
  fetchWorkflows,
  extractHtmlContent,
  deleteTemplate,
  updateTemplate,
  saveWorkflow,
  applyChanges,
  getNodeType,
} from '../services/portalService';
import styles from '../styles/PortalBuilder.module.css';
import AutocompleteTextarea from './AutocompleteTextarea';
import TextInput from '../../common/components/TextInput/TextInput';
import AddressTemplate from '../../CustomTemplate/AddressTemplate/AddressTemplate';
import DropdownButton from '../../common/components/Dropdown/Dropdown';
import DefaultTemplate from '../../CustomTemplate/Default/DefaultTemplate';

import {
  uploadImageToAWS,
  downloadImageFromAWS,
} from '../services/imageService';
import { base64Encode } from '../../../utils/base64Utils';

const PortalBuilder = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { token, handleLogout } = useAuth();
  const {
    template,
    template_name,
    template_json,
    template_status,
    template_id,
    wfw_id,
    wfw_node_id,
    delete_status,
    node_type,
  } = location.state || {};
  const [htmlContent, setHtmlContent] = useState(template || '');
  const [userPrompt, setUserPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [workflowId, setWorkflowId] = useState(wfw_id || '');
  const [nodeId, setNodeId] = useState(wfw_node_id || '');
  const [templateName, setTemplateName] = useState(template_name || '');
  const [templateStatus, setTemplateStatus] = useState(
    template_status || 'draft'
  );
  const [dropdownLabel, setDropdownLabel] = useState(
    template_status || 'Draft'
  );
  const [selected, setSelected] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  let templateJson = {};
  try {
    templateJson = JSON.parse(template_json) || {};
  } catch (error) {}
  const [title, setTitle] = useState(templateJson.title || 'title');
  const [subTitle, setSubTitle] = useState(templateJson.subTitle || '');
  const [containerStyle, setContainerStyle] = useState(
    templateJson.containerStyle || {}
  );
  const [workflows, setWorkflows] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [nodeType, setNodeType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [selectedImage, setSelectedImage] = useState(null);
  const containerRef = useRef(null);
  const [uploadImage, setUploadImage] = useState('');

  const [state, setState] = useState({
    heading: '',
    description: '',
    header1: '',
    header2: '',
    bannerDesc: '',
    bannerButton: '',
  });
  const options = ['Draft', 'Publish'];
  const hasExtractedInitialStyles = useRef(false);

  // Helper function to convert CSS property names to React camelCase
  const cssToReactStyle = (cssProp) => {
    return cssProp.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  function formatNodeType(nodeType) {
    let trimmedString = nodeType.endsWith('Node')
      ? nodeType.slice(0, -4)
      : nodeType;
    const formattedString = trimmedString.replace(/([A-Z])/g, ' $1').trim();
    return formattedString;
  }

  useEffect(() => {
    if (!containerRef.current) return;

    // const elements = {
    //   heading: 'h2[class*="heading"]',
    //   description: 'p[class*="sub-heading"]',
    //   header1: 'h3[class*="package-title"]',
    //   header2: 'h1[class*="main-title"]',
    //   bannerDesc: 'p[class*="description"]',
    //   bannerButton: 'button[class*="shop-button"]',
    // };

    // const newState = Object.entries(elements).reduce((acc, [key, selector]) => {
    //   const element = containerRef.current.querySelector(selector);
    //   acc[key] = element?.innerHTML || '';
    //   return acc;
    // }, {});

    //   setState((prev) => {
    //     if (JSON.stringify(prev) === JSON.stringify(newState)) {
    //       return prev; // Prevents unnecessary updates and infinite loops
    //     }
    //     return newState;
    //   });
    setNodeType(getNodeType(node_type));
  }, [template, node_type, htmlContent]);

  // Extract styles from PageContainer on initial load only
  useEffect(() => {
    if (htmlContent && !hasExtractedInitialStyles.current) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Look for PageContainer or wrapper elements with inline styles
      const containerSelectors = [
        '.selfSchedulingNode-wrapper',
        '.selfSchedulingNode-Container',
        '.chooseProductsNode-Container',
        '.checkForServiceNode-Container',
        '.accountSetupNode-Container',
        '.addOnsNode-Container',
        '.paymentNode-Container',
        '.successNode-Container',
        '[class*="Container"]',
        '[class*="wrapper"]',
      ];

      let wrapper = null;
      for (const selector of containerSelectors) {
        wrapper = tempDiv.querySelector(selector);
        if (wrapper && wrapper.style && wrapper.style.length > 0) {
          break;
        }
      }

      if (wrapper && wrapper.style && wrapper.style.length > 0) {
        const styleObj = {};
        for (let i = 0; i < wrapper.style.length; i++) {
          const prop = wrapper.style[i];
          const camelProp = cssToReactStyle(prop);
          styleObj[camelProp] = wrapper.style.getPropertyValue(prop);
        }
        setContainerStyle(styleObj);
        hasExtractedInitialStyles.current = true;
      }
    }
  }, [htmlContent]);

  useEffect(() => {
    const updateImage = async () => {
      try {
        const imageData = await downloadImageFromAWS(
          workflowId,
          nodeId,
          token,
          handleLogout
        );

        if (!imageData) {
          console.error('Failed to download image');
          return;
        }

        console.log('Image Data:', imageData);

        if (containerRef.current) {
          containerRef.current.innerHTML =
            containerRef.current.innerHTML.replace(
              /<img\s+[^>]*src="([^"]+)"[^>]*>/,
              `
  <style>
    img[src*="${imageData.split('/').pop()}"] {
      height: 40px !important;
      width: auto !important;
      max-width: 100% !important;
      object-fit: contain !important;
      display: block !important;
    }
  </style>
  <img src="${imageData}" alt="image" />
  `
            );
        }
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };

    updateImage();
  }, []);
  useEffect(() => {
    fetchWorkflows(token, setWorkflows);
  }, [token]);

  const handleSave = async (temp_status) => {
    setIsLoading(true);
    if (!templateName || !htmlContent) {
      setSnackbarMessage('Template name and content are required.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    let contentToSave = containerRef.current
      ? containerRef.current.innerHTML
      : htmlContent;

    // Inject containerStyle back into the PageContainer before saving (for all node types)
    if (Object.keys(containerStyle).length > 0) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentToSave;

      // Look for the appropriate container element
      const containerSelectors = [
        '.selfSchedulingNode-wrapper',
        '.selfSchedulingNode-Container',
        '.chooseProductsNode-Container',
        '.checkForServiceNode-Container',
        '.accountSetupNode-Container',
        '.addOnsNode-Container',
        '.paymentNode-Container',
        '.successNode-Container',
        '[class*="Container"]',
        '[class*="wrapper"]',
      ];

      let wrapper = null;
      for (const selector of containerSelectors) {
        wrapper = tempDiv.querySelector(selector);
        if (wrapper) break;
      }

      if (wrapper) {
        // Apply containerStyle to wrapper (convert camelCase back to CSS format)
        Object.keys(containerStyle).forEach((camelProp) => {
          const cssProp = camelProp.replace(/([A-Z])/g, '-$1').toLowerCase();
          wrapper.style.setProperty(cssProp, containerStyle[camelProp]);
        });
        contentToSave = tempDiv.innerHTML;
      }
    }

    if (containerRef.current) {
      setHtmlContent(contentToSave);
    }
    try {
      /*const result = await uploadImageToAWS(uploadImage, workflowId, nodeId, token, handleLogout);
  const imageData = await downloadImageFromAWS(workflowId, nodeId, token, handleLogout);
  if (imageData)  console.log("Image Data:", imageData);
  if (!imageData)  console.error("Failed to download image")
  containerRef.current.innerHTML = containerRef.current.innerHTML.replace(
    /<img[^>]+src="([^"]+)"[^>]*>/,
    `<img src="${imageData}" alt="Updated Image" 
            style="width: 100%; height: 100%; object-fit: cover;">
    `
  );*/

      const endpoint = template_id ? `update/${template_id}` : 'create';
      const payload = {
        template_name: templateName,
        template: base64Encode(contentToSave),
        template_status: temp_status,
        template_json: JSON.stringify({ title, subTitle }),
        ...(template_id ? {} : { wfw_id: workflowId, wfw_node_id: nodeId }),
      };
      //console.log(containerRef.current.innerHTML,'containerRef.current.innerHTML')

      const response = await saveWorkflow(
        template_id,
        endpoint,
        token,
        payload
      );
      if (response && response.status === 200) {
        setSnackbarMessage(
          temp_status === 'publish'
            ? 'Template published successfully!'
            : 'Template saved successfully!'
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        if (temp_status === 'publish') {
          setIsLoading(true);
          setTimeout(() => {
            navigate(`/workflow/${wfw_id}`, { state: { workflowId: wfw_id } });
            setIsLoading(false);
          }, 4000);
        }
        setIsLoading(false);
      } else {
        setSnackbarMessage(
          temp_status === 'publish'
            ? 'Workflow publish failed'
            : 'Template save failed'
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsLoading(false);
      }
    } catch (error) {
      const errorMsg =
        error.response?.status === 409
          ? 'Template with the same workflow and node ID already exists.'
          : 'Error saving template.';
      setSnackbarMessage(errorMsg);
      setSnackbarSeverity('error');
      console.error('Error saving template:', error);
      setIsLoading(false);
    }
  };

  const handleApplyChanges = async () => {
    setIsLoading(true);
    const prompt = `${userPrompt}`;
    try {
      const response = await applyChanges(htmlContent, prompt, token);
      if (response && response.data) {
        let extractedHtml = extractHtmlContent(response.data);

        // Extract styles from PageContainer for all node types
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = extractedHtml;

        // Look for PageContainer or wrapper elements with inline styles
        const containerSelectors = [
          '.selfSchedulingNode-wrapper',
          '.selfSchedulingNode-Container',
          '.chooseProductsNode-Container',
          '.checkForServiceNode-Container',
          '.accountSetupNode-Container',
          '.addOnsNode-Container',
          '.paymentNode-Container',
          '.successNode-Container',
          '[class*="Container"]',
          '[class*="wrapper"]',
        ];

        let wrapper = null;
        for (const selector of containerSelectors) {
          wrapper = tempDiv.querySelector(selector);
          if (wrapper && wrapper.style && wrapper.style.length > 0) {
            break;
          }
        }

        if (wrapper && wrapper.style && wrapper.style.length > 0) {
          const styleObj = {};
          // Extract all inline styles and convert to React camelCase
          for (let i = 0; i < wrapper.style.length; i++) {
            const prop = wrapper.style[i];
            const camelProp = cssToReactStyle(prop);
            styleObj[camelProp] = wrapper.style.getPropertyValue(prop);
          }

          // Remove from wrapper to avoid duplication
          wrapper.removeAttribute('style');
          extractedHtml = tempDiv.innerHTML;

          // Update both states together
          if (response.status === 200) {
            setHtmlContent(extractedHtml);
            setContainerStyle(styleObj);
            setSnackbarMessage('Changes applied successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }
        } else if (response.status === 200) {
          setHtmlContent(extractedHtml);
          setSnackbarMessage('Changes applied successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }
      }
    } catch (error) {
      console.error('Error generating HTML:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!template_id) return;
    try {
      await deleteTemplate(template_id, token);
      setSnackbarMessage('Template deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/portal-builder-templates');
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const resetChanges = () => {
    setHtmlContent(template);
  };

  const handlePermanentDelete = async () => {
    if (!template_id) return;
    try {
      const response = await deleteTemplate(template_id, token);

      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setSnackbarMessage('Template deleted permanently!');
      navigate('/portal-builder-templates', {
        state: {
          delete_status_back: 'not null',
        },
      });
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleUserPromptChange = (value) => {
    setUserPrompt(value);
  };

  const handleImageUploadChange = async (value) => {
    setUploadImage(value);
    const result = await uploadImageToAWS(
      value,
      workflowId,
      nodeId,
      token,
      handleLogout
    );
    let imageData = await downloadImageFromAWS(
      workflowId,
      nodeId,
      token,
      handleLogout
    );
    if (imageData)
      if (!imageData) {
        //console.log("Image Data:", imageData);
        //console.error("Failed to download image");
        imageData = '/images/address-img.png'; // Set default image path
      }
    containerRef.current.innerHTML = containerRef.current.innerHTML.replace(
      /<img\s+[^>]*src="([^"]+)"[^>]*>/,
      `
  <style>
    img[src*="${imageData.split('/').pop()}"] {
      height: 40px !important;
      width: auto !important;
      max-width: 100% !important;
      object-fit: contain !important;
      display: block !important;
    }
  </style>
  <img src="${imageData}" alt="image" />
  `
    );
    // handleSave("publish");
    const endpoint = template_id ? `update/${template_id}` : 'create';
    const payload = {
      template_name: templateName,
      template: containerRef.current
        ? base64Encode(containerRef.current.innerHTML)
        : base64Encode(htmlContent),
      template_status: 'publish',
      template_json: JSON.stringify({
        title,
        subTitle,
        containerStyle: Object.keys(containerStyle).length > 0 ? containerStyle : undefined,
      }),
      ...(template_id ? {} : { wfw_id: workflowId, wfw_node_id: nodeId }),
    };

    const response = await saveWorkflow(template_id, endpoint, token, payload);
  };

  const handleInputChange = (key, value) => {
    setState((prev) => {
      const newState = { ...prev, [key]: value };

      if (containerRef.current) {
        const elementSelectors = {
          heading: 'h2[class*="heading"]',
          description: 'p[class*="sub-heading"]',
          header1: 'h3[class*="package-title"]',
          header2: 'h1[class*="main-title"]',
          bannerDesc: 'p[class*="description"]',
          bannerButton: 'button[class*="shop-button"]',
        };

        const selector = elementSelectors[key];
        if (selector) {
          const element = containerRef.current.querySelector(selector);
          if (element) {
            element.innerText = newState[key];
          }
        }
      }

      return newState;
    });
  };

  const getNodeTemplate = () => {
    switch (node_type) {
      case 'addressNode1':
        return (
          <AddressTemplate
            userPrompt={userPrompt}
            onUserPromptChange={handleUserPromptChange}
            onhandleInputChange={handleInputChange}
            onApplyChanges={handleApplyChanges}
            heading={state.heading}
            description={state.description}
            header1={state.header1}
            header2={state.header2}
            bannerDesc={state.bannerDesc}
            bannerButton={state.bannerButton}
            workflowId={wfw_id}
            onImageUpload={handleImageUploadChange}
          />
        );
      case 'selfSchedulingNode':
        return (
          <DefaultTemplate
            userPrompt={userPrompt}
            onUserPromptChange={handleUserPromptChange}
            onApplyChanges={handleApplyChanges}
            nodeType={nodeType}
            workflowId={wfw_id}
          />
        );
      default:
        return (
          <DefaultTemplate
            userPrompt={userPrompt}
            onUserPromptChange={handleUserPromptChange}
            onApplyChanges={handleApplyChanges}
            nodeType={nodeType}
            workflowId={wfw_id}
          />
        );
    }
  };

  return (
    <>
      {isLoading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}
      <div className={styles.container}>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
        </Snackbar>

        <>
          <div className={styles.sidebar}>
            <h3 className={styles.sidebarH3}>
              <input
                type="text"
                placeholder="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </h3>

            {getNodeTemplate()}

            {false && template_id && !delete_status && (
              <button onClick={handleDelete} className={styles.deleteButton}>
                Delete Template
              </button>
            )}

            {false && template_id && delete_status && (
              <button
                onClick={handlePermanentDelete}
                className={styles.deleteParmanent}
              >
                Delete Permanently
              </button>
            )}

            {errorMessage && <div className={styles.error}>{errorMessage}</div>}
            <div className={styles.buttonContainer}>
              <div className={styles.buttonGroup}>
                {!delete_status && (
                  <>
                    <button
                      onClick={() => handleSave('save')}
                      className={styles.buttonPrimary}
                    >
                      Save as Draft
                    </button>
                    <button
                      onClick={() => handleSave('publish')}
                      className={styles.buttonPrimary}
                    >
                      Publish
                    </button>
                    {/* {
                  <div style={{ position: "relative", paddingLeft: 10 }}>
                    <DropdownButton
                      label={dropdownLabel}
                      options={options}
                      onSelect={handleSelect}
                      direction="upward"
                      style={{ marginBottom: "00px" }}
                    />
                  </div>
                } */}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.content} style={containerStyle}>
            {loading && <div className={styles.loader}></div>}

            <div
              className={styles.preview}
              ref={containerRef}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        </>
      </div>
    </>
  );
};

export default PortalBuilder;
